import { useCallback, useEffect, useMemo } from 'react';
import { useQueryStates, parseAsString, parseAsTimestamp, parseAsStringLiteral } from 'nuqs';
import { type RangeGranularity, getAdjustedGranularity } from '@/utils/granularity.ts';

type DashboardGranularity = Exclude<RangeGranularity, '1min' | '15min'>;

const GRANULARITIES = ['5min', 'hourly', 'daily', 'monthly'] as const;
const HIDDEN_GRANULARITIES: RangeGranularity[] = ['1min', '15min'];

const performanceMonitoringParsers = {
  view: parseAsString.withDefault('all'),
  from: parseAsTimestamp,
  to: parseAsTimestamp,
  granularity: parseAsStringLiteral(GRANULARITIES).withDefault('5min'),
};

function calculateDaysDifference(from: Date, to: Date | undefined): number {
  const endDate = to ?? from;
  const diffTime = Math.abs(endDate.getTime() - from.getTime());
  const days = diffTime / (1000 * 60 * 60 * 24);

  return Math.max(1, Math.ceil(days));
}

export function usePerformanceMonitoringParams() {
  const [params, setParams] = useQueryStates(performanceMonitoringParsers, {
    history: 'replace',
  });

  const defaultStartDate = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);
  const defaultEndDate = useMemo(() => new Date(new Date().setHours(23, 59, 59, 999)), []);

  const startDate = params.from ?? defaultStartDate;
  const endDate = params.to ?? defaultEndDate;

  useEffect(() => {
    if (params.from && params.to) {
      const days = calculateDaysDifference(params.from, params.to);
      const adjusted = getAdjustedGranularity(
        days,
        params.granularity,
        HIDDEN_GRANULARITIES
      ) as DashboardGranularity;

      if (adjusted !== params.granularity) {
        void setParams({ granularity: adjusted });
      }
    }
  }, [params.from, params.to]);

  const currentViewContext = params.view;

  const dateRange = useMemo(
    () => ({
      startDate,
      endDate: endDate as Date | undefined,
      rangeGranularity: params.granularity as DashboardGranularity,
    }),
    [startDate, endDate, params.granularity]
  );

  const setCurrentViewContext = useCallback(
    (view: string) => {
      void setParams({ view });
    },
    [setParams]
  );

  const setDateRange = useCallback(
    (range: {
      startDate: Date;
      endDate: Date | undefined;
      rangeGranularity: DashboardGranularity;
    }) => {
      void setParams({
        from: range.startDate,
        to: range.endDate ?? null,
        granularity: range.rangeGranularity,
      });
    },
    [setParams]
  );

  return {
    currentViewContext,
    setCurrentViewContext,
    dateRange,
    setDateRange,
  };
}
